import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 450;
const CATALOG_CLOSE_DELAY = 500;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#scheduleClose(), {
      signal: this.#abortController.signal,
    });
    this.overflowMenu?.addEventListener('pointerenter', () => this.#cancelClose(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the More overflow panel itself is hovered.
   * Do not use the whole overflow-list: the open dropdown sits over the product title,
   * so treating the list as hovered keeps Franchise / More from fully closing.
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#cancelClose();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#cancelClose();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');
    const reopening = this.hasAttribute('data-submenu-closing');
    this.removeAttribute('data-submenu-closing');

    if (!(event.target instanceof Element)) return;

    // Open a column only when the pointer is on the label, not the padded gap beside it.
    if (event.type === 'pointerenter' && !event.target.closest('.menu-list__link-title, .menu-list__link')) {
      return;
    }

    let item = findMenuItem(event.target);

    if (!item || (item == this.#state.activeItem && !reopening)) return;

    const isDefaultSlot = event.target.closest('[slot]')?.getAttribute('slot') !== 'more';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.#setPanelPointerEvents('');

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay.
   * Do not hide immediately: the pointer needs time to reach submenu links.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      const next = 'relatedTarget' in event ? event.relatedTarget : null;
      if (next instanceof Node && (item.parentElement?.contains(next) || this.overflowMenu?.contains(next))) {
        return;
      }
      this.#scheduleClose();
    }
  }

  #cancelClose() {
    this.#closeTimer && clearTimeout(this.#closeTimer);
    this.#closeTimer = 0;
  }

  #scheduleClose() {
    this.#cancelClose();
    const delay = this.#closeDelay();
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = 0;
      this.#deactivate();
    }, delay);
  }

  /**
   * Catalog is a taller mega menu, so it stays open a little longer.
   * More / Franchise still wait long enough to click a link.
   * @returns {number}
   */
  #closeDelay() {
    const item = this.#state.activeItem;
    const submenu = item ? findSubmenu(item) : null;
    if (submenu?.querySelector('.mega-menu__grid--catalog')) return CATALOG_CLOSE_DELAY;
    return DEACTIVATE_DELAY;
  }

  /** @type {number} */
  #closeTimer = 0;

  /**
   * Hide open panels now so they cannot stay painted over the product title.
   * @param {HTMLElement | null} [item]
   */
  #collapseVisual(item = this.#state.activeItem) {
    this.dataset.submenuClosing = 'true';
    this.#setPanelPointerEvents('none');
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.ariaExpanded = 'false';
    if (item) item.ariaExpanded = 'false';
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.#pointerStillInside()) return;

    this.#collapseVisual(item);
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
    item.removeAttribute('data-animating');
  };

  /**
   * @returns {boolean}
   */
  #pointerStillInside() {
    const item = this.#state.activeItem;
    const submenu = item ? findSubmenu(item) : null;
    return (
      this.overflowHovered ||
      (submenu?.matches(':hover') ?? false) ||
      (item?.parentElement?.matches(':hover') ?? false)
    );
  }

  /**
   * @param {string} value
   */
  #setPanelPointerEvents(value) {
    const panels = this.querySelectorAll('.menu-list__submenu');
    panels.forEach((panel) => {
      if (panel instanceof HTMLElement) panel.style.pointerEvents = value;
    });
    if (this.overflowMenu) this.overflowMenu.style.pointerEvents = value;
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const more = element.closest('[slot="more"]');
  if (more) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(more.parentElement?.querySelector('[slot="overflow"]'));
  }

  const item = element.closest('.menu-list__list-item') ?? element;
  const menuitem = item.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
